import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { FormattedMessage } from 'react-intl';
import FormattedPrice from '../../global/FormattedPrice/FormattedPrice';
import { graphql } from 'react-relay';
import { unmaskFragmentData } from '../../utils/relayUnmaskingHelper';

const LOCAL_QUOTE = 'LOCAL_PICKUP';

const REGION_CODES = {
    REST_OF_WORLD: 'REST_OF_WORLD',
};

const REGION_TYPES = {
    DOMESTIC: 'DOMESTIC',
    LOCAL: 'LOCAL',
};

// eslint-disable-next-line no-unused-vars
const shippingFragment = graphql`
    fragment shippingHelpers_shipmentQuote on ShipmentQuote {
        handlingTimeDisplay
        totalAmount {
            currency
            amount
            convertedAmountList {
                currency
                amount
            }
        }
        buyerSubTotalAmount {
            convertedAmountList {
                currency
                amount
            }
        }
        buyerServiceMethodDisplay
        serviceMethod {
            serviceMethodType
            region {
                apiType
                code
                regionName
                regionType
            }
        }
    }
`;

export const shippingQuoteConnectionFragment = graphql`
    fragment shippingHelpers_shipmentQuoteConnection on ShipmentQuoteConnection {
        edges {
            node {
                ...shippingHelpers_shipmentQuote
                totalAmount {
                    currency
                    amount
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                buyerSubTotalAmount {
                    convertedAmountList {
                        currency
                        amount
                    }
                }
                serviceMethod {
                    serviceMethodType
                    region {
                        apiType
                        code
                        regionName
                        regionType
                    }
                }
            }
        }
    }
`;

export const shippingQuotesConnectionFragment = graphql`
    fragment shippingHelpers_shipmentQuotesConnection on ShipmentQuotesConnection {
        edges {
            node {
                ...shippingHelpers_shipmentQuote
            }
        }
    }
`;

function getShipmentFragmentData({ relayContext, relayData, fragmentPropName, fragment }) {
    return unmaskFragmentData({
        relayContext,
        relayData,
        fragmentPropName,
        fragment,
    });
}

/**
 * Used for clearing edges for a relay node.
 *
 * @param {Object} node - Relay node containing edges
 * @return {Array}
 */
const cleanRelayFields = node => node.edges.map(edge => edge.node);

/**
 * Tells if a given quote has local pickup shipping.
 *
 * @param {Object} quote
 * @return {Boolean}
 */
const isLocalPickup = quote => get(quote, 'serviceMethod.serviceMethodType') !== LOCAL_QUOTE;

/**
 * Filters quotes with same region & method.
 * Priorities lower cost and internal quotes.
 *
 * @param {Array} quotes - A Relay list of shipping quotes, already filtered by nodes.
 * @return {Array}
 */
export const sortMultipleQuotes = quotes => {
    return quotes.reduce((sortedQuotes, quote) => {
        const index = sortedQuotes.findIndex(
            q =>
                get(q, 'serviceMethod.serviceType') === get(quote, 'serviceMethod.serviceType') &&
                get(q, 'serviceMethod.serviceMethodType') ===
                    get(quote, 'serviceMethod.serviceMethodType') &&
                get(q, 'serviceMethod.region.code') === get(quote, 'serviceMethod.region.code')
        );

        if (index !== -1) {
            const newQuoteAmount = get(quote, 'totalAmount.amount');
            const existingQuoteAmount = get(sortedQuotes[index], 'totalAmount.amount');
            const hasLowerAmount = newQuoteAmount < existingQuoteAmount;
            const hasSameAmount = newQuoteAmount === existingQuoteAmount;
            const is1stdibsBilledQuote = get(quote, 'is1stdibsBilledQuote');

            if (hasLowerAmount || (hasSameAmount && is1stdibsBilledQuote)) {
                sortedQuotes.splice(index, 1, quote);
            }
        } else {
            sortedQuotes.push(quote);
        }
        return sortedQuotes;
    }, []);
};

/**
 * Filters out local pickup and multiple shipping quotes for a given list of shipping quotes.
 *
 * @param {Array} quotes - A Relay list of shipping quotes.
 * @return {Array}
 */
export const getFilteredQuotes = (quotes = []) => sortMultipleQuotes(quotes);

/**
 * Returns formatted shipping price.
 *
 * @param {Array} convertedAmountList - List of converted amounts.
 * @param {String} currency - User currency.
 * @returns {String}
 */
export const getShippingPrice = ({ convertedAmountList, currency }) => {
    const { amount } = convertedAmountList.find(p => p.currency === currency); // find amount by currency
    let price = <FormattedPrice currency={currency} value={amount} />;

    if (amount === 0) {
        price = (
            <FormattedMessage id="shippingHelpers.complimentary" defaultMessage="Complimentary" />
        );
    }
    return price;
};

const getHandlingTimeCopy = ({ buyerServiceMethodDisplay, handlingTimeDisplay }) => {
    return buyerServiceMethodDisplay && handlingTimeDisplay
        ? `${buyerServiceMethodDisplay}: ${handlingTimeDisplay}`
        : null;
};

const ShippingText = ({
    serviceMethod,
    convertedAmountList,
    currency,
    regionType,
    isLocalPrimary,
}) => {
    let regionName = get(serviceMethod, 'region.regionName');
    const regionCode = get(serviceMethod, 'region.code');
    const price = getShippingPrice({ convertedAmountList, currency });

    if (regionType === REGION_TYPES.LOCAL && !isLocalPrimary) {
        return (
            <FormattedMessage
                id="shippingHelpers.local_price"
                defaultMessage="Local shipping starts at {price}"
                values={{ price }}
            />
        );
    }

    if (regionCode === REGION_CODES.REST_OF_WORLD) {
        regionName = (
            <FormattedMessage
                id="shippingHelpers.rest_of_world"
                defaultMessage="anywhere in the world"
            />
        );
    }

    return (
        <FormattedMessage
            id="shippingHelpers.shipping_to"
            defaultMessage="{price} to {regionName}"
            values={{ price, regionName }}
        />
    );
};

ShippingText.propTypes = {
    convertedAmountList: PropTypes.array,
    currency: PropTypes.string,
    isLocalPrimary: PropTypes.bool,
    regionType: PropTypes.string,
    serviceMethod: PropTypes.object,
};

function getTotalAmountInCurrency(quote, currency) {
    const amountObj = (get(quote, 'totalAmount.convertedAmountList') || []).find(
        amount => currency === amount.currency
    );
    if (amountObj) {
        return amountObj.amount;
    }
    return 0;
}

function sortQuotes(quotes, currency) {
    return [...quotes] // copy to avoid mutating quotes
        .sort((quoteA, quoteB) => {
            const aAmount = getTotalAmountInCurrency(quoteA, currency);
            const bAmount = getTotalAmountInCurrency(quoteB, currency);
            return aAmount - bAmount;
        });
}

export const getCheapestQuote = (quotes, currency) => {
    //find the cheapest quote based on the user's currency
    const filteredQuotes = quotes ? cleanRelayFields(quotes).filter(isLocalPickup) : [];
    const cheapestQuote = sortQuotes(filteredQuotes, currency)[0] || null;
    return cheapestQuote;
};

const getValidQuote = ({ quotes, regionType, currency, isLocalPrimary = false }) => {
    const firstQuote = getCheapestQuote(quotes, currency);

    if (firstQuote) {
        const {
            buyerServiceMethodDisplay,
            handlingTimeDisplay,
            serviceMethod,
            buyerSubTotalAmount,
        } = firstQuote;

        return {
            // We have some additional copy choices for specific handling types.
            handling: getHandlingTimeCopy({ buyerServiceMethodDisplay, handlingTimeDisplay }),
            output: (
                <ShippingText
                    convertedAmountList={buyerSubTotalAmount.convertedAmountList}
                    currency={currency}
                    isLocalPrimary={isLocalPrimary}
                    regionType={regionType}
                    serviceMethod={serviceMethod}
                />
            ),
        };
    }

    return { output: null };
};

const getQuoteSecondary = ({ localDeliveryOptions, domesticDeliveryOptions, currency }) => {
    if (localDeliveryOptions.edges.length && domesticDeliveryOptions.edges.length) {
        const { output: quoteSecondary } = getValidQuote({
            quotes: localDeliveryOptions,
            regionType: REGION_TYPES.LOCAL,
            currency,
        });

        if (quoteSecondary) {
            return quoteSecondary;
        }
    }
    return null;
};

const getQuotePrimary = ({
    localDeliveryOptions,
    domesticDeliveryOptions,
    shipmentQuotes,
    currency,
}) => {
    const { output: outputDomestic, handling: handlingDomestic } = getValidQuote({
        quotes: domesticDeliveryOptions,
        regionType: REGION_TYPES.DOMESTIC,
        currency,
    });

    const { output: outputLocal, handling: handlingLocal } = getValidQuote({
        quotes: localDeliveryOptions,
        regionType: REGION_TYPES.LOCAL,
        isLocalPrimary: true,
        currency,
    });

    if (outputDomestic) {
        return { output: outputDomestic, handling: handlingDomestic, hasQuote: true };
    } else if (outputLocal) {
        return { output: outputLocal, handling: handlingLocal, hasQuote: true };
    }

    const globalQuotes = shipmentQuotes
        ? cleanRelayFields(shipmentQuotes).filter(isLocalPickup)[0]
        : null;

    return {
        output: (
            <FormattedMessage
                id="pdpShippingHelpers.global_available"
                defaultMessage="Global Shipping Available"
            />
        ),
        handling: null,
        hasQuote: !!globalQuotes,
    };
};

export const buildShippingDetails = ({
    localDeliveryOptions,
    domesticDeliveryOptions,
    shipmentQuotes,
    currency,
    relayContext,
}) => {
    localDeliveryOptions = getShipmentFragmentData({
        relayContext,
        relayData: localDeliveryOptions,
        fragmentPropName: 'shipmentQuoteConnection',
        fragment: shippingQuoteConnectionFragment,
    });
    domesticDeliveryOptions = getShipmentFragmentData({
        relayContext,
        relayData: domesticDeliveryOptions,
        fragmentPropName: 'shipmentQuoteConnection',
        fragment: shippingQuoteConnectionFragment,
    });
    shipmentQuotes = getShipmentFragmentData({
        relayContext,
        relayData: shipmentQuotes,
        fragmentPropName: 'shipmentQuotesConnection',
        fragment: shippingQuotesConnectionFragment,
    });
    const { output, handling, hasQuote } = getQuotePrimary({
        localDeliveryOptions,
        domesticDeliveryOptions,
        shipmentQuotes,
        currency,
    });

    let additional = null;

    if (handling) {
        additional =
            getQuoteSecondary({ localDeliveryOptions, domesticDeliveryOptions, currency }) || '';
    }

    return {
        output,
        handling,
        hasQuote,
        additional,
    };
};
